import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Timer } from "lucide-react";
import { ACTS, readDemo, writeDemo, type DemoState } from "@/features/demo/tour";

/** Floating presenter bar — follows the presenter across every page. */
export default function DemoBar() {
  const [state, setState] = useState<DemoState>(() => readDemo());
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const sync = () => setState(readDemo());
    window.addEventListener("wekit-demo", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("wekit-demo", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [state]);

  if (!state) return null;
  const act = ACTS[state.act] ?? ACTS[0];
  const secs = Math.max(0, Math.floor((now - state.startedAt) / 1000));
  const time = `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;

  const go = (i: number) => {
    const next = { ...state, act: i };
    writeDemo(next);
    navigate(ACTS[i].route);
  };

  return (
    <aside
      aria-label="Presenter tour"
      className="no-print fixed bottom-4 left-1/2 z-50 w-[min(94vw,560px)] -translate-x-1/2 rounded-3xl border border-border bg-background/90 p-3 shadow-2xl backdrop-blur"
    >
      <div className="flex items-center gap-2">
        <button
          onClick={() => go(Math.max(0, state.act - 1))}
          disabled={state.act === 0}
          aria-label="Previous act"
          className="grid h-11 w-11 place-items-center rounded-full hover:bg-muted disabled:opacity-40"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button onClick={() => setOpen((o) => !o)} className="min-h-11 flex-1 text-left" aria-expanded={open}>
          <span className="block text-xs text-muted-foreground">Act {act.n} of {ACTS.length} · {act.minutes} min</span>
          <span className="block font-semibold">{act.title}</span>
        </button>
        <span className="flex items-center gap-1 text-sm tabular-nums text-muted-foreground" role="timer">
          <Timer className="h-4 w-4" aria-hidden /> {time}
        </span>
        <button
          onClick={() => go(Math.min(ACTS.length - 1, state.act + 1))}
          disabled={state.act === ACTS.length - 1}
          aria-label="Next act"
          className="grid h-11 w-11 place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <button onClick={() => writeDemo(null)} aria-label="End tour" className="grid h-11 w-11 place-items-center rounded-full hover:bg-muted">
          <X className="h-5 w-5" />
        </button>
      </div>
      {open && (
        <div className="mt-2 border-t border-border pt-3">
          <ul className="space-y-1 text-sm">
            {act.say.map((s) => <li key={s}>• {s}</li>)}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            {act.stops.map((s) => (
              <Link key={s.route} to={s.route} className="inline-flex min-h-11 items-center rounded-full border border-border px-4 text-sm hover:bg-muted">
                {s.label}
              </Link>
            ))}
            <Link to="/demo" className="inline-flex min-h-11 items-center rounded-full px-4 text-sm text-muted-foreground hover:bg-muted">
              All acts
            </Link>
          </div>
        </div>
      )}
    </aside>
  );
}
